import { reportTypesForFeatures, type ReportType } from "@visioni/a3s-reports/client-messages";
import type { TenantFeatures } from "@src/features/tenants";

/**
 * Which report tiles a tenant is entitled to, as a phase rather than a list.
 *
 * `pending` is kept distinct from an empty `ready` on purpose. Collapsing them
 * would render the "no reports" empty state on every cold deep-link before the
 * camera list arrives, and then flip to tiles - the same failure the
 * clip-compilation access hook's docblock describes for `pending` vs `denied`.
 */
export type ReportEntitlements =
  | { readonly status: "pending" }
  | { readonly status: "error"; readonly message: string }
  | { readonly status: "ready"; readonly reportTypes: readonly ReportType[] };

/**
 * Who is looking, as far as the tiles care.
 *
 * - `tenant`: every tile the tenant's hires grant (`roadsense_camera_access` off).
 * - `camera`: `roadsense_camera_access` on. RoadSense opens to anyone whose
 *   cameras are hired for it, but WasteSense stays tenant-admin only, so this
 *   needs the admin answer; `isTenantAdmin` is `undefined` until it is known.
 */
export type ReportViewer =
  | { readonly access: "tenant" }
  | { readonly access: "camera"; readonly isTenantAdmin: boolean | undefined };

/** Report types only a tenant admin can open, whatever their cameras. */
const ADMIN_ONLY_REPORT_TYPES: ReadonlySet<ReportType> = new Set(["wastesense"]);

/**
 * Project the features of the cameras the user can see onto the reports they
 * grant this viewer.
 *
 * Pure, so it can be unit tested without the React Native module graph.
 */
export function resolveReportEntitlements(features: TenantFeatures, viewer: ReportViewer): ReportEntitlements {
  if (features.status === "pending") return { status: "pending" };
  if (features.status === "error") return { status: "error", message: features.message };

  const reportTypes = reportTypesForFeatures([...features.held]);
  if (viewer.access === "tenant") return { status: "ready", reportTypes };

  // Like the sidebar's other admin-gated entries, show nothing until we know.
  if (viewer.isTenantAdmin === undefined) return { status: "pending" };
  if (viewer.isTenantAdmin) return { status: "ready", reportTypes };
  return { status: "ready", reportTypes: reportTypes.filter((type) => !ADMIN_ONLY_REPORT_TYPES.has(type)) };
}
